import { z } from "zod";

import { query } from "@archipad-js/core/orm";
import { createEntityWithProperties, updateEntityWithProperties } from "@archipad-js/core/orm";

import {
    CalendarModificationEntity,
    DocumentEntity,
    ProjectEntity,
    RegionEntity,
    BugEntity,
    ProjectUserEntity,
    VisitEntity,
} from "@archipad-js/archipad-model";

import { BugNoteType } from "./bug-note-type";

export class DocumentService {
    listDocuments(options: {
        where: {
            project: ProjectEntity;
            region?: RegionEntity;
        };
        orderBy?: string;
    }): DocumentEntity[] {
        const { project, region } = options.where;

        const entityContext = project.getContext();
        const q = query<DocumentEntity>(entityContext, "Document")
            .predicateWithFormat("owningProject == {project}", { project })
            .orderBy(options.orderBy ?? "id asc");

        if (region) {
            q.predicateWithFormat("region == {region}", { region });
        }

        const resultSet = q.execute();
        return resultSet.sortedEntities();
    }

    updateDocument(documentEntity: DocumentEntity, payload: UpdateDocumentPayload): void {
        const instancePayload = UpdateDocumentPayloadSchema.parse(payload);
        if (payload.downloadPolicy !== undefined) {
            updateEntityWithProperties(documentEntity.activeVersion, { downloadPolicy: payload.downloadPolicy });
        }
        updateEntityWithProperties(documentEntity, instancePayload);
    }

    createBugDocument(
        project: ProjectEntity,
        bug: BugEntity,
        currentVisit: VisitEntity | null,
        user: ProjectUserEntity,
        filename: string,
        size: number,
        mimeType: string,
    ): DocumentEntity {
        const entityContext = project.getContext();
        const localizationService = entityContext.localizationService;
        const document = createEntityWithProperties(entityContext, "Document", {
            downloadPolicy: DOWNLOAD_POLICY.enum.ON_DEMAND,
            owningProject: project,
            name: filename,
            bug: bug,
        });
        createEntityWithProperties(entityContext, "DocumentVersion", {
            downloadPolicy: DOWNLOAD_POLICY.enum.ON_DEMAND,
            filename: filename,
            index: "1",
            date: currentVisit?.date ?? new Date(),
            mimeType: mimeType,
            size: size.toString(),
            user,
            document: document,
            activeForDocument: document,
        });
        createEntityWithProperties(project.getContext(), "BugNote", {
            creationDate: new Date(),
            date: currentVisit?.date ?? new Date(),
            bug: bug,
            visit: currentVisit,
            user: user,
            type: BugNoteType.TYPE_HISTORY_DOCUMENT,
            text: localizationService.l("New Document '{{documentName}}' was added", {
                documentName: filename,
            }),
        });
        return document;
    }

    deleteBugDocument(document: DocumentEntity, user: ProjectUserEntity, visit?: VisitEntity): void {
        const entityContext = user.getContext();
        const localizationService = entityContext.localizationService;
        createEntityWithProperties(entityContext, "BugNote", {
            creationDate: new Date(),
            bug: document.bug as BugEntity,
            visit: visit,
            user: user,
            type: BugNoteType.TYPE_HISTORY_DOCUMENT,
            text: localizationService.l("Document '{{documentName}}' was deleted", {
                documentName: document.name ?? "Unknown Document",
            }),
        });
        entityContext.deleteEntity(document);
    }

    createCalendarModificationDocument(
        project: ProjectEntity,
        calendarModification: CalendarModificationEntity,
        user: ProjectUserEntity,
        filename: string,
        size: number,
        mimeType: string,
    ): DocumentEntity {
        const entityContext = project.getContext();
        const document = createEntityWithProperties(entityContext, "Document", {
            downloadPolicy: DOWNLOAD_POLICY.enum.ON_DEMAND,
            owningProject: project,
            name: filename,
            calendarModification: calendarModification,
        });
        createEntityWithProperties(entityContext, "DocumentVersion", {
            downloadPolicy: DOWNLOAD_POLICY.enum.ON_DEMAND,
            filename: filename,
            index: "1",
            date: new Date(),
            mimeType: mimeType,
            size: size.toString(),
            user,
            document: document,
            activeForDocument: document,
        });
        return document;
    }
}

export const DOWNLOAD_POLICY = z.enum(["ON_DEMAND", "FORCE"]);
export type DOWNLOAD_POLICY = z.infer<typeof DOWNLOAD_POLICY>;

export const UpdateDocumentPayloadSchema = z.object({
    name: z.string().optional(),
    downloadPolicy: DOWNLOAD_POLICY.optional(),
});
export type UpdateDocumentPayload = z.infer<typeof UpdateDocumentPayloadSchema>;
